import { useMutation, useQuery, useQueryClient } from 'react-query'
import { store } from 'react-notifications-component'
import {
  getPaymentMethods,
  getPaymentMethod,
  getSiteConfigs,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
  updateSiteConfig,
  deleteSiteConfig,
} from '@services/queries'
import {
  PAYMENT_METHOD_CREATE_SUCCESS_MESSAGE,
  PAYMENT_METHOD_CREATE_FAILURE_MESSAGE,
  PAYMENT_METHOD_UPDATE_SUCCESS_MESSAGE,
  PAYMENT_METHOD_UPDATE_FAILURE_MESSAGE,
  PAYMENT_METHOD_DELETE_SUCCESS_MESSAGE,
  PAYMENT_METHOD_DELETE_FAILURE_MESSAGE,
  SITE_CONFIG_SAVE_SUCCESS_MESSAGE,
  SITE_CONFIG_SAVE_FAILURE_MESSAGE,
  SITE_CONFIG_DELETE_SUCCESS_MESSAGE,
  SITE_CONFIG_DELETE_FAILURE_MESSAGE,
} from '@constants'

export function usePaymentMethodsQuery(filters = {}) {
  return useQuery(
    [
      'payment-methods',
      filters.siteId,
      filters.providerId,
      filters.type,
      filters.enabled,
    ],
    () => getPaymentMethods(filters),
  )
}

export function usePaymentMethodQuery(id) {
  return useQuery(['payment-method', id], () => getPaymentMethod(id), {
    enabled: !!id,
  })
}

export function useSiteConfigsQuery(id) {
  return useQuery(['site-configs', id], () => getSiteConfigs(id), {
    enabled: !!id,
  })
}

export function useCreatePaymentMethodMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  return useMutation((payload) => createPaymentMethod(payload), {
    onSuccess(data) {
      queryClient.invalidateQueries(['payment-methods'])
      store.addNotification(PAYMENT_METHOD_CREATE_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data)
    },
    onError(err) {
      store.addNotification(PAYMENT_METHOD_CREATE_FAILURE_MESSAGE)
      if (onError) onError(err)
    },
  })
}

export function useUpdatePaymentMethodMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  return useMutation(({ id, payload }) => updatePaymentMethod(id, payload), {
    onSuccess(data, variables) {
      queryClient.invalidateQueries(['payment-methods'])
      queryClient.invalidateQueries(['payment-method', variables.id])
      store.addNotification(PAYMENT_METHOD_UPDATE_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data)
    },
    onError(err) {
      store.addNotification(PAYMENT_METHOD_UPDATE_FAILURE_MESSAGE)
      if (onError) onError(err)
    },
  })
}

export function useTogglePaymentMethodEnabledMutation() {
  const queryClient = useQueryClient()

  return useMutation(({ id, payload }) => updatePaymentMethod(id, payload), {
    onMutate: async ({ id, payload }) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['payment-methods'] })

      // Snapshot all payment-methods queries for rollback
      const previousQueries = queryClient.getQueriesData(['payment-methods'])

      // Optimistically update all matching cached queries
      queryClient.setQueriesData(['payment-methods'], (old) => {
        if (!old) return old
        return old.map((pm) => (pm.id === id ? { ...pm, ...payload } : pm))
      })

      return { previousQueries }
    },
    onError: (err, variables, context) => {
      // Roll back to the previous cache state
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      store.addNotification(PAYMENT_METHOD_UPDATE_FAILURE_MESSAGE)
    },
    onSettled: () => {
      // Refetch to ensure server state is in sync
      queryClient.invalidateQueries(['payment-methods'])
    },
  })
}

export function useDeletePaymentMethodMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  return useMutation((id) => deletePaymentMethod(id), {
    onSuccess(data) {
      queryClient.invalidateQueries(['payment-methods'])
      store.addNotification(PAYMENT_METHOD_DELETE_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data)
    },
    onError(err) {
      store.addNotification(PAYMENT_METHOD_DELETE_FAILURE_MESSAGE)
      if (onError) onError(err)
    },
  })
}

export function useUpdateSiteConfigMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  return useMutation(
    ({ id, siteId, payload }) => updateSiteConfig(id, siteId, payload),
    {
      onSuccess(data, variables) {
        queryClient.invalidateQueries(['payment-method', variables.id])
        queryClient.invalidateQueries(['site-configs', variables.id])
        store.addNotification(SITE_CONFIG_SAVE_SUCCESS_MESSAGE)
        if (onSuccess) onSuccess(data)
      },
      onError(err) {
        store.addNotification(SITE_CONFIG_SAVE_FAILURE_MESSAGE)
        if (onError) onError(err)
      },
    },
  )
}

export function useDeleteSiteConfigMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  return useMutation(({ id, siteId }) => deleteSiteConfig(id, siteId), {
    onSuccess(data, variables) {
      queryClient.invalidateQueries(['payment-method', variables.id])
      queryClient.invalidateQueries(['site-configs', variables.id])
      store.addNotification(SITE_CONFIG_DELETE_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data)
    },
    onError(err) {
      store.addNotification(SITE_CONFIG_DELETE_FAILURE_MESSAGE)
      if (onError) onError(err)
    },
  })
}
